import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { access, apiErrorSchema, isoDateTime } from './schemas/common.js';
import { questionOptionStatusSchema } from './schemas/domain.js';
import { chatMessageSchema, chatSendSchema, chatStreamEventSchema, conversationSchema, conversationSummarySchema, uploadedAttachmentSchema, } from './schemas/chat.js';
const c = initContract();
/** A multipart upload: one file under `file`. The API reads it; the web sends FormData. */
const upload = z.object({ file: z.unknown() });
const byId = z.object({ id: z.string() });
/**
 * The panel's chat. Everything is for dispatchers and up; each person reaches
 * their own conversations and an admin everyone's — someone else's is a 404.
 */
export const chatContract = c.router({
    scopes: {
        method: 'GET',
        metadata: access('dispatcher'),
        path: '/chat/scopes',
        summary: 'The five sources a question can be asked of, each marked available here',
        responses: { 200: z.object({ scopes: z.array(questionOptionStatusSchema) }) },
    },
    conversations: {
        method: 'GET',
        metadata: access('dispatcher'),
        path: '/chat/conversations',
        summary: 'Conversations, newest activity first — everybody’s for an admin',
        query: z.object({
            limit: z.coerce.number().int().min(1).max(100).default(30),
            before: isoDateTime.optional(),
        }),
        responses: {
            200: z.object({
                items: z.array(conversationSummarySchema),
                nextBefore: isoDateTime.optional(),
            }),
        },
    },
    conversation: {
        method: 'GET',
        metadata: access('dispatcher'),
        path: '/chat/conversations/:id',
        summary: 'One conversation and its messages, oldest first',
        pathParams: byId,
        responses: {
            200: z.object({ conversation: conversationSchema, messages: z.array(chatMessageSchema) }),
            404: apiErrorSchema,
        },
    },
    remove: {
        method: 'DELETE',
        metadata: access('dispatcher'),
        path: '/chat/conversations/:id',
        summary: 'Forget a conversation: one’s own, or any for an admin',
        pathParams: byId,
        body: c.noBody(),
        responses: { 204: c.noBody(), 404: apiErrorSchema },
    },
    stream: {
        method: 'POST',
        metadata: access('dispatcher'),
        path: '/chat/stream',
        summary: 'Ask in a conversation; the answer arrives as server-sent events',
        body: chatSendSchema,
        responses: {
            200: c.otherResponse({ contentType: 'text/event-stream', body: chatStreamEventSchema }),
            400: apiErrorSchema,
            404: apiErrorSchema,
        },
    },
    attach: {
        method: 'POST',
        metadata: access('dispatcher'),
        path: '/chat/attachments',
        summary: 'Upload a PDF, TXT, CSV or Markdown file (≤ 10 MB) for the next question',
        contentType: 'multipart/form-data',
        body: upload,
        responses: {
            201: z.object({ attachment: uploadedAttachmentSchema }),
            400: apiErrorSchema,
            413: apiErrorSchema,
            415: apiErrorSchema,
            422: apiErrorSchema,
        },
    },
    transcribe: {
        method: 'POST',
        metadata: access('dispatcher'),
        path: '/chat/transcribe',
        summary: 'A spoken question to text: 16 kHz mono WAV, ≤ 2 MB',
        contentType: 'multipart/form-data',
        body: upload,
        responses: {
            200: z.object({ text: z.string() }),
            400: apiErrorSchema,
            413: apiErrorSchema,
            415: apiErrorSchema,
            502: apiErrorSchema,
            503: apiErrorSchema,
        },
    },
});
//# sourceMappingURL=chat.contract.js.map